import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { C, ff, t } from '../Dashboard/components/dashboardConstants';
import styles from './PortfolioPdfEditorPage.module.css';
import {
  getMyTalentProfile, getTalentDetail,
  getTalentPdfLayout, saveTalentPdfLayout,
  updateTalentProfile,
  addTalentWork, updateTalentWork, deleteTalentWork,
  addTalentMedia, deleteTalentMedia,
  uploadFile,
} from '../../../api';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';

// 필모그래피는 A4 한 장에 4열 × 2행 = 8작품이 들어간다.
// 작품이 더 많으면 '필모그래피 페이지 추가'로 지면을 늘린다.
const WORKS_PER_PAGE = 8;
const MAX_FILMO_PAGES = 5;
const MAX_FILMOGRAPHY = WORKS_PER_PAGE * MAX_FILMO_PAGES;

const PHOTOS_PER_PAGE = 6;   // 3열 × 2행
const MAX_PHOTO_PAGES = 5;

/** 문서를 구성하는 지면 목록. 좌측은 이 순서대로 세로로 이어 붙인 하나의 문서다. */
const buildPages = (filmoPages, photoPages) => [
  { id: 'basic', kind: 'basic', label: '기본 정보' },
  ...Array.from({ length: filmoPages }, (_, i) => ({
    id: `filmography-${i}`,
    kind: 'filmography',
    index: i,
    label: filmoPages > 1 ? `필모그래피 ${i + 1}` : '필모그래피',
  })),
  ...Array.from({ length: photoPages }, (_, i) => ({
    id: `photos-${i}`,
    kind: 'photos',
    index: i,
    label: photoPages > 1 ? `추가 사진 ${i + 1}` : '추가 사진',
  })),
];
// PDF에는 작품당 대표 스틸컷 1장만 싣는다 (프로필 편집 화면은 4장까지 허용)
const MAX_STILLCUTS = 1;
const PHOTO_SLOTS = [
  ['1행 1열', '1행 2열', '1행 3열'],
  ['2행 1열', '2행 2열', '2행 3열'],
];

// 프로필 편집(EditFilmographySection)과 동일한 매체 유형 목록
const MEDIA_TYPES = [
  '장편상업영화', '장편독립영화', '단편영화', 'TV/OTT',
  '숏폼드라마', '웹드라마', '광고', '기타',
];

function TopStepper({ steps, activeStep, onStepClick }) {
  return (
    <div className={styles.stepper}>
      {steps.map((step, i) => {
        const active = activeStep === step.id;
        return (
          <button
            key={step.id}
            onClick={() => onStepClick(step.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0,
              padding: '10px 16px', borderRadius: '100px', border: `1px solid ${C.g200}`,
              background: active ? C.bk : C.white,
              cursor: 'pointer',
            }}
          >
            <span style={{
              width: 20, height: 20, borderRadius: '50%',
              background: active ? C.white : C.g200,
              color: active ? C.bk : C.sub,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: 700, fontFamily: ff, flexShrink: 0,
            }}>
              {i + 1}
            </span>
            <span style={t('14px', 600, active ? C.white : C.bk)}>{step.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <span style={t('13px', 600, C.sub)}>{label}</span>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', height: '46px', padding: '0 16px', border: `1px solid ${C.g200}`,
  borderRadius: '10px', fontFamily: ff, fontSize: '15px', outline: 'none', boxSizing: 'border-box',
};

const textareaStyle = {
  ...inputStyle, height: 'auto', minHeight: '90px', padding: '14px 16px', resize: 'vertical',
};

// 최근 연도 우선 정렬 — 편집 중 연도를 바꿔도 미리보기가 즉시 반영되도록 렌더 시점에 적용
const byRecentYear = (a, b) => (Number(b.year) || 0) - (Number(a.year) || 0);

// 편집 패널과 미리보기의 페이지 경계를 맞추려면 '같은 순서'로 잘라야 한다.
// 그래서 페이지 분할 기준은 정렬만 한 목록(orderedWorks)이고,
// 미리보기는 그 페이지 안에서 숨김·미입력 작품만 걸러 낸다.
const orderedWorks = (works) => [...works].sort(byRecentYear);
const isPrintableWork = (w) => Boolean(w.title) && !w.hidden;
const visibleWorks = (works) => orderedWorks(works).filter(isPrintableWork);

/** 지면 추가 버튼 — 해당 종류의 마지막 지면 바로 아래에 놓인다 */
function AddPageButton({ label, onClick, disabled }) {
  return (
    <div className={styles.pageActions}>
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          height: '46px', borderRadius: '10px', border: `1px dashed ${C.g200}`,
          background: C.white,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span style={{ fontSize: '16px', color: C.bk }}>+</span>
        <span style={t('14px', 600, C.bk)}>{label}</span>
      </button>
    </div>
  );
}

/** 사용자가 프로토콜 없이 "youtube.com/..."로 입력하는 경우가 많아 보정한다 */
const toExternalUrl = (raw) => {
  const v = (raw || '').trim();
  if (!v) return null;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(v) ? v : `https://${v}`;
};

/** 영상 링크 아이콘 — 실제 <a>라서 인쇄된 PDF에서도 클릭하면 열린다 */
function VideoLink({ url, size = 10, color = C.g400 }) {
  const href = toExternalUrl(url);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title="영상 보기"
      onClick={(e) => e.stopPropagation()}
      style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.7 1.7M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7l1.7-1.7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </a>
  );
}

/* ── Page 1: 기본 정보 미리보기 ── */
function BasicInfoPreview({ form, works = [] }) {
  const filmography = visibleWorks(works);
  const hasBio = Boolean(form.bio && form.bio.trim());
  const hasSkills = form.skills.length > 0;

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', padding: '0' }}>
      {/* 대표 사진 — 4:5 비율로 지면 높이를 꽉 채운다.
          폭을 %로 잡으면 4:5 높이가 지면보다 짧아 위아래 여백이 생기므로,
          높이를 100%로 고정하고 폭을 비율에서 역산한다 (지면의 약 57%). */}
      <div style={{ height: '100%', aspectRatio: '4 / 5', flexShrink: 0, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {form.mainPhoto ? (
          <img src={form.mainPhoto} alt="대표 이미지" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <span style={t('13px', 400, C.g300)}>대표 이미지를 추가하세요</span>
        )}
      </div>
      <div style={{ flex: 1, padding: '28px 30px', display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
        {/* 예시값(email@email.com 등)을 폴백으로 두면 그대로 인쇄되므로,
            값이 없는 항목은 렌더하지 않는다 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div>
            {form.englishName && <div style={t('12px', 400, C.g300)}>{form.englishName}</div>}
            <div style={t('26px', 700, C.bk)}>{form.name}</div>
            {(form.heightCm || form.weightKg) && (
              <div style={t('13px', 400, C.sub)}>
                {[form.heightCm && `${form.heightCm}cm`, form.weightKg && `${form.weightKg}kg`]
                  .filter(Boolean).join(' / ')}
              </div>
            )}
            {form.birthDate && <div style={t('13px', 400, C.sub)}>{form.birthDate}</div>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
            {form.email && <span style={t('12px', 400, C.g400)}>{form.email}</span>}
            {form.phone && <span style={t('12px', 400, C.g400)}>{form.phone}</span>}
            {form.instagram && <span style={t('12px', 400, C.g400)}>@{form.instagram.replace(/^@+/, '')}</span>}
          </div>
        </div>

        {/* 내용이 없는 섹션은 제목까지 통째로 감춘다 —
            안내용 회색 문구가 그대로 인쇄되면 안 되기 때문 */}
        {hasBio && (
          <div style={{ borderTop: `1px solid ${C.g200}`, paddingTop: '16px' }}>
            <div style={t('12px', 600, C.g400)}>자기소개</div>
            <p style={{ ...t('13px', 400, C.bk), marginTop: '6px', whiteSpace: 'pre-wrap' }}>
              {form.bio}
            </p>
          </div>
        )}

        {hasSkills && (
          <div style={{ borderTop: `1px solid ${C.g200}`, paddingTop: '14px' }}>
            <div style={t('12px', 600, C.g400)}>특기</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
              {form.skills.map(s => (
                <span key={s} style={{ padding: '4px 10px', borderRadius: '100px', border: `1px solid ${C.g200}`, ...t('11px', 500, C.bk) }}>{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* 필모그래피 — 특기 하단, 최근 연도순 */}
        {filmography.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.g200}`, paddingTop: '14px', minHeight: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {filmography.map((w, i) => (
                <div key={w.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ ...t('12px', 500, C.g400), width: '32px', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                    {w.year || '----'}
                  </span>
                  {w.mediaType && (
                    <span style={{ padding: '3px 8px', borderRadius: '6px', border: `1px solid ${C.bk}`, flexShrink: 0, ...t('10px', 500, C.bk) }}>
                      {w.mediaType}
                    </span>
                  )}
                  <span style={{ ...t('13px', 700, C.bk), flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {w.title}
                  </span>
                  {w.role && (
                    <span style={{ ...t('12px', 400, C.sub), flexShrink: 1, minWidth: 0, maxWidth: '35%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {w.role}
                    </span>
                  )}
                  <VideoLink url={w.videoUrl} size={11} />
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

/* ── 필모그래피 페이지 — 이미 잘린 한 페이지분(pageWorks)을 받는다 ── */
function FilmographyPreview({ pageWorks, pageLabel, pageNumber, onSelectWork }) {
  // 클릭 시 편집 패널의 몇 번째 카드인지 알아야 하므로 페이지 내 원래 인덱스를 유지한다
  const visible = pageWorks
    .map((w, i) => ({ work: w, indexInPage: i }))
    .filter(({ work }) => isPrintableWork(work));

  return (
    <div style={{ width: '100%', height: '100%', padding: '22px 28px', display: 'flex', flexDirection: 'column', gap: '10px', boxSizing: 'border-box', position: 'relative' }}>
      <div style={t('12px', 600, C.g400)}>{pageLabel}</div>

      {visible.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={t('13px', 400, C.g300)}>필모그래피를 추가하세요.</span>
        </div>
      ) : (
        // 4열 × 2행 = 8작품이 A4 한 장에 들어가도록 고정.
        // 열 수를 고정해야 작품이 적어도 카드가 늘어나지 않는다.
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '10px',
          alignContent: 'start',
          minHeight: 0,
        }}>
          {visible.map(({ work: w, indexInPage }, i) => {
            const cover = (w.stillcuts || [])[0];
            return (
              <div
                key={w.id ?? i}
                onClick={onSelectWork ? () => onSelectWork(indexInPage) : undefined}
                style={{
                  border: `1px solid ${C.g200}`, borderRadius: '8px', overflow: 'hidden',
                  display: 'flex', flexDirection: 'column', background: C.white, minWidth: 0,
                  cursor: onSelectWork ? 'pointer' : 'default',
                }}
              >
                {/* 스틸컷 — 1:1이어야 정보 영역까지 포함해 2행이 지면에 들어간다 */}
                <div style={{ width: '100%', aspectRatio: '1 / 1', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {cover ? (
                    <img src={cover.preview} alt={w.title || '스틸컷'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={t('10px', 400, C.g300)}>스틸컷</span>
                  )}
                </div>

                {/* 작품 정보 */}
                <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: '2px', borderTop: `1px solid ${C.g200}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ padding: '1px 4px', borderRadius: '3px', border: `1px solid ${C.g200}`, flexShrink: 0, ...t('8px', 500, C.sub) }}>
                      {w.mediaType || '분류'}
                    </span>
                    <span style={{ ...t('9px', 500, C.g400), fontVariantNumeric: 'tabular-nums' }}>{w.year || '----'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                    <span style={{ ...t('11px', 600, C.bk), flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {w.title || '작품명'}
                    </span>
                    <VideoLink url={w.videoUrl} size={9} />
                  </div>
                  <div style={{ ...t('9px', 400, C.sub), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {w.role || '역할명'}{w.company ? ` · ${w.company}` : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pageNumber != null && (
        <span style={{ position: 'absolute', right: '14px', bottom: '10px', ...t('10px', 400, C.g300) }}>
          {pageNumber}
        </span>
      )}
    </div>
  );
}

/* ── 추가 사진: 위치·배율 모델 ──────────────────────────────────────────────
   슬롯 값은 { url, x, y, scale }.
   x·y는 '컨테이너 크기 대비 퍼센트'라서, 작은 미리보기 칸과 큰 조정 화면,
   인쇄용 확대 지면에서 모두 같은 구도가 나온다 (px로 두면 크기마다 어긋난다).
   과거 저장분은 URL 문자열이므로 읽을 때 정규화한다. */
const PHOTO_MIN_SCALE = 1;
const PHOTO_MAX_SCALE = 3;

const normalizePhoto = (p) => {
  if (!p) return null;
  if (typeof p === 'string') return { url: p, mediaId: null, x: 0, y: 0, scale: 1 };
  if (!p.url) return null;
  return {
    url: p.url,
    mediaId: p.mediaId ?? null,
    file: p.file ?? null,        // 있으면 아직 업로드되지 않은 사진
    x: Number.isFinite(p.x) ? p.x : 0,
    y: Number.isFinite(p.y) ? p.y : 0,
    scale: Number.isFinite(p.scale) ? p.scale : 1,
  };
};

/** layoutJson에는 File 객체를 넣을 수 없다 (JSON.stringify가 {}로 만든다) */
const serializePhoto = (p) => {
  const n = normalizePhoto(p);
  if (!n) return null;
  return { url: n.url, mediaId: n.mediaId, x: n.x, y: n.y, scale: n.scale };
};

/** 배율 1에서는 이미지가 칸에 꼭 맞으므로 이동 여백이 없다. 확대한 만큼만 움직일 수 있다. */
const panLimit = (scale) => Math.max(0, (scale - 1) * 50);
const clampPan = (v, scale) => {
  const m = panLimit(scale);
  return Math.min(m, Math.max(-m, v));
};
const clampScale = (v) => Math.min(PHOTO_MAX_SCALE, Math.max(PHOTO_MIN_SCALE, v));

/** 조정값이 반영된 사진 한 장 — 미리보기 칸과 조정 화면이 같은 렌더러를 쓴다 */
function PhotoFrame({ photo, alt }) {
  if (!photo) return null;
  return (
    <img
      src={photo.url}
      alt={alt}
      draggable={false}
      style={{
        width: '100%', height: '100%', objectFit: 'cover', display: 'block',
        transform: `translate(${photo.x}%, ${photo.y}%) scale(${photo.scale})`,
        transformOrigin: 'center',
        userSelect: 'none',
      }}
    />
  );
}

/** 드래그로 이동, 휠로 확대·축소하는 조정 화면 */
function PhotoAdjuster({ photo, onChange }) {
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // 휠은 passive 리스너로 붙으면 preventDefault가 막혀 패널이 함께 스크롤된다.
  // React onWheel 대신 네이티브로 { passive: false } 등록.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      onChange((cur) => {
        const scale = clampScale(cur.scale - e.deltaY * 0.0015);
        return { ...cur, scale, x: clampPan(cur.x, scale), y: clampPan(cur.y, scale) };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onChange]);

  function handlePointerDown(e) {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, w: rect.width, h: rect.height };
    setDragging(true);
  }

  function handlePointerMove(e) {
    const d = dragRef.current;
    if (!d) return;
    const dxPct = ((e.clientX - d.px) / d.w) * 100;
    const dyPct = ((e.clientY - d.py) / d.h) * 100;
    dragRef.current = { ...d, px: e.clientX, py: e.clientY };
    onChange((cur) => ({
      ...cur,
      x: clampPan(cur.x + dxPct, cur.scale),
      y: clampPan(cur.y + dyPct, cur.scale),
    }));
  }

  function endDrag(e) {
    if (!dragRef.current) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    dragRef.current = null;
    setDragging(false);
  }

  const movable = panLimit(photo.scale) > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <span style={t('13px', 600, C.sub)}>
        위치·크기 조정 <span style={t('12px', 400, C.g400)}>(드래그: 이동 / 휠: 확대·축소)</span>
      </span>
      <div
        ref={boxRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          width: '100%', aspectRatio: '1 / 1',
          borderRadius: '10px', border: `1px solid ${C.g200}`,
          overflow: 'hidden', background: C.bg,
          cursor: movable ? (dragging ? 'grabbing' : 'grab') : 'default',
          touchAction: 'none',
        }}
      >
        <PhotoFrame photo={photo} alt="위치 조정" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={t('12px', 500, C.sub)}>배율 {Math.round(photo.scale * 100)}%</span>
        <input
          type="range"
          min={PHOTO_MIN_SCALE * 100}
          max={PHOTO_MAX_SCALE * 100}
          value={Math.round(photo.scale * 100)}
          onChange={(e) => {
            const scale = clampScale(Number(e.target.value) / 100);
            onChange((cur) => ({ ...cur, scale, x: clampPan(cur.x, scale), y: clampPan(cur.y, scale) }));
          }}
          style={{ flex: 1 }}
        />
        <button
          onClick={() => onChange((cur) => ({ ...cur, x: 0, y: 0, scale: 1 }))}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, ...t('12px', 600, C.sub) }}
        >
          초기화
        </button>
      </div>

      {!movable && (
        <p style={t('12px', 400, C.g300)}>확대하면 위치를 옮길 수 있습니다.</p>
      )}
    </div>
  );
}

/* ── Page 3: 추가 사진 미리보기 ── */
function PhotosPreview({ photos, slotOffset = 0, selectedSlot, onSlotClick, pageNumber }) {
  return (
    // 셀을 맞붙이고 구분선을 긋는 대신 gap으로 띄운다 — 지면 가장자리 여백도 함께 준다
    <div style={{
      position: 'relative',
      width: '100%', height: '100%',
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: 'repeat(2, 1fr)',
      gap: '10px',
      padding: '14px',
      boxSizing: 'border-box',
    }}>
      {PHOTO_SLOTS.flat().map((label, i) => {
        const idx = slotOffset + i;   // photos는 모든 페이지가 이어진 하나의 배열
        const photo = normalizePhoto(photos[idx]);
        const isSelected = selectedSlot === idx;
        return (
          <div
            key={idx}
            onClick={() => onSlotClick(idx)}
            style={{
              position: 'relative',
              // 사진이 없는 칸은 테두리·안내문 없이 흰 여백으로 남긴다 —
              // 안내문이나 빈 상자가 그대로 PDF에 인쇄되면 안 되기 때문.
              // 선택된 칸만 편집용으로 테두리를 보여준다.
              border: isSelected ? `1px solid ${C.bk}` : (photo ? `1px solid ${C.g200}` : '1px solid transparent'),
              borderRadius: '6px',
              background: C.white,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', overflow: 'hidden',
              minWidth: 0, minHeight: 0,
            }}
          >
            <PhotoFrame photo={photo} alt={label} />
          </div>
        );
      })}
      {pageNumber != null && (
        <span style={{ position: 'absolute', right: '14px', bottom: '6px', ...t('10px', 400, C.g300) }}>
          {pageNumber}
        </span>
      )}
    </div>
  );
}

/* ── 기본 정보 편집 패널 ── */
function BasicInfoPanel({ form, updateField, onPhotoChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <FieldRow label="대표 이미지">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: 72, height: 96, borderRadius: '10px', background: C.bg, border: `1px solid ${C.g200}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {form.mainPhoto ? (
              <img src={form.mainPhoto} alt="대표" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={t('11px', 400, C.g300)}>없음</span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', border: `1px solid ${C.g200}`, borderRadius: '10px', cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5-5 5 5M12 5v12" stroke={C.bk} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span style={t('13px', 600, C.bk)}>{form.mainPhoto ? '이미지 변경' : '이미지 첨부'}</span>
              <input
                type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    updateField('mainPhoto', URL.createObjectURL(file));
                    onPhotoChange(file);
                  }
                  e.target.value = '';
                }}
              />
            </label>
            {form.mainPhoto && (
              <button
                onClick={() => { updateField('mainPhoto', null); onPhotoChange(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: `1px solid ${C.g200}`, borderRadius: '10px', cursor: 'pointer', background: 'none' }}
              >
                <span style={t('13px', 500, C.sub)}>삭제</span>
              </button>
            )}
          </div>
        </div>
      </FieldRow>

      <div style={{ display: 'flex', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <FieldRow label="이름">
            <input style={inputStyle} value={form.name} onChange={e => updateField('name', e.target.value)} />
          </FieldRow>
        </div>
        <div style={{ flex: 1 }}>
          <FieldRow label="영문 이름">
            <input style={inputStyle} value={form.englishName} onChange={e => updateField('englishName', e.target.value)} placeholder="English Name" />
          </FieldRow>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <FieldRow label="키 (cm)">
            <input style={inputStyle} value={form.heightCm} onChange={e => updateField('heightCm', e.target.value)} />
          </FieldRow>
        </div>
        <div style={{ flex: 1 }}>
          <FieldRow label="몸무게 (kg)">
            <input style={inputStyle} value={form.weightKg} onChange={e => updateField('weightKg', e.target.value)} />
          </FieldRow>
        </div>
        <div style={{ flex: 1 }}>
          <FieldRow label="생년월일">
            <input type="date" style={inputStyle} value={form.birthDate} onChange={e => updateField('birthDate', e.target.value)} />
          </FieldRow>
        </div>
      </div>

      <FieldRow label="이메일">
        <input style={inputStyle} value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="name@email.com" />
      </FieldRow>

      <FieldRow label="연락처">
        <input style={inputStyle} value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="010-0000-0000" />
      </FieldRow>

      <FieldRow label="인스타그램">
        <input style={inputStyle} value={form.instagram} onChange={e => updateField('instagram', e.target.value)} placeholder="@instagram" />
      </FieldRow>

      <FieldRow label="자기소개">
        <textarea style={textareaStyle} value={form.bio} onChange={e => updateField('bio', e.target.value)} placeholder="자기소개를 입력하세요." />
      </FieldRow>

      <FieldRow label="특기">
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            value={form.skillDraft}
            onChange={e => updateField('skillDraft', e.target.value)}
            placeholder="특기 입력 후 추가"
            onKeyDown={e => {
              if (e.key === 'Enter' && form.skillDraft.trim()) {
                updateField('skills', [...form.skills, form.skillDraft.trim()]);
                updateField('skillDraft', '');
              }
            }}
          />
          <button
            onClick={() => {
              if (form.skillDraft.trim()) {
                updateField('skills', [...form.skills, form.skillDraft.trim()]);
                updateField('skillDraft', '');
              }
            }}
            style={{ width: 46, height: 46, borderRadius: '10px', border: 'none', background: C.bk, color: C.white, cursor: 'pointer', flexShrink: 0, fontSize: '20px', lineHeight: 1 }}
          >
            +
          </button>
        </div>
        {form.skills.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
            {form.skills.map((s, i) => (
              <span key={s + i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '100px', border: `1px solid ${C.g200}`, ...t('12px', 500, C.bk) }}>
                {s}
                <button
                  onClick={() => updateField('skills', form.skills.filter((_, idx) => idx !== i))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: C.sub }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </FieldRow>
    </div>
  );
}

/* ── 작품 카드 (편집 패널용) ── */
function WorkCard({ work, index, onUpdate, onRemove, cardRef, highlighted }) {
  const stillcuts = work.stillcuts || [];
  const canAddMore = stillcuts.length < MAX_STILLCUTS;

  // 업로드는 저장 시점에 일괄 처리 — 여기서는 미리보기용 objectURL만 만든다
  function addStillcut(file) {
    if (!file) return;
    onUpdate('stillcuts', [...stillcuts, { mediaId: null, preview: URL.createObjectURL(file), file }]);
  }

  function removeStillcut(idx) {
    const shot = stillcuts[idx];
    if (shot.mediaId) {
      onUpdate('_deletedStillcutMediaIds', [...(work._deletedStillcutMediaIds || []), shot.mediaId]);
    }
    onUpdate('stillcuts', stillcuts.filter((_, i) => i !== idx));
  }

  return (
    <div
      ref={cardRef}
      style={{
        display: 'flex', flexDirection: 'column', gap: '12px',
        padding: '16px', borderRadius: '12px',
        border: `1px solid ${highlighted ? C.bk : C.g200}`,
        boxShadow: highlighted ? `0 0 0 2px ${C.bk}22` : 'none',
        transition: 'border-color .2s, box-shadow .2s',
        opacity: work.hidden ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={t('13px', 700, C.bk)}>작품 {index + 1}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => onUpdate('hidden', !work.hidden)}
            title={work.hidden ? 'PDF에 표시' : 'PDF에서 숨기기'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
          >
            {work.hidden ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.9 4.2A9.5 9.5 0 0112 4c5 0 9 4.5 9 8a9.8 9.8 0 01-2.3 3.9M6.6 6.6C4.4 8 3 10.3 3 12c0 3.5 4 8 9 8 1.4 0 2.7-.3 3.9-.9" stroke={C.sub} strokeWidth="1.6" strokeLinecap="round"/></svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" stroke={C.sub} strokeWidth="1.6"/><circle cx="12" cy="12" r="2.6" stroke={C.sub} strokeWidth="1.6"/></svg>
            )}
          </button>
          <button onClick={onRemove} title="작품 삭제" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.sub} strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
        </div>
      </div>

      {/* 스틸컷 — 첫 장이 PDF 대표 이미지로 쓰인다 */}
      <FieldRow label="대표 스틸컷 (1장)">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {stillcuts.map((shot, si) => (
            <div key={shot.mediaId ?? shot.preview} style={{ position: 'relative', width: 62, height: 62, borderRadius: '8px', overflow: 'hidden', border: `1px solid ${C.g200}`, flexShrink: 0 }}>
              <img src={shot.preview} alt="스틸컷" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <button
                onClick={() => removeStillcut(si)}
                style={{ position: 'absolute', top: 3, right: 3, width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
              >
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"/></svg>
              </button>
            </div>
          ))}
          {canAddMore && (
            <label style={{
              width: 62, height: 62, borderRadius: '8px', border: `1px dashed ${C.g200}`,
              background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke={C.g400} strokeWidth="1.8" strokeLinecap="round"/></svg>
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { addStillcut(e.target.files[0]); e.target.value = ''; }} />
            </label>
          )}
        </div>
      </FieldRow>

      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ width: '100px', flexShrink: 0 }}>
          <FieldRow label="촬영 연도">
            <input style={inputStyle} value={work.year} onChange={e => onUpdate('year', e.target.value)} placeholder="2024" />
          </FieldRow>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FieldRow label="제작사/플랫폼">
            <input style={inputStyle} value={work.company} onChange={e => onUpdate('company', e.target.value)} placeholder="예: tvN" />
          </FieldRow>
        </div>
      </div>

      <FieldRow label="작품 제목">
        <input style={inputStyle} value={work.title} onChange={e => onUpdate('title', e.target.value)} placeholder="작품명을 입력하세요" />
      </FieldRow>

      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FieldRow label="매체 유형">
            <select style={inputStyle} value={work.mediaType} onChange={e => onUpdate('mediaType', e.target.value)}>
              <option value="">선택</option>
              {MEDIA_TYPES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </FieldRow>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FieldRow label="배역명">
            <input style={inputStyle} value={work.role} onChange={e => onUpdate('role', e.target.value)} placeholder="예: 김지수 역" />
          </FieldRow>
        </div>
      </div>

      <FieldRow label="영상 링크">
        <input
          style={inputStyle}
          value={work.videoUrl ?? ''}
          onChange={e => onUpdate('videoUrl', e.target.value)}
          placeholder="YouTube, Vimeo 등 영상 URL"
        />
      </FieldRow>
      {work.videoUrl && (
        <p style={t('12px', 400, C.g400)}>
          PDF의 작품 카드에 링크 아이콘이 붙고, 눌러서 영상을 열 수 있습니다.
        </p>
      )}
    </div>
  );
}

/* ── 필모그래피 편집 패널 ── */
/**
 * 한 페이지분 작품만 편집한다. 목록은 연도순으로 정렬된 뒤 잘리므로
 * 인덱스가 아니라 객체 동일성으로 갱신·삭제해야 엉뚱한 작품이 바뀌지 않는다.
 */
function FilmographyPanel({
  pageWorks, pageIndex, totalWorks, setWorks, onWorkDeleted,
  canAddWork, focusedIndex, onFocusHandled,
}) {
  const hiddenCount = pageWorks.filter(w => w.hidden).length;
  const cardRefs = useRef({});

  // 미리보기에서 작품을 클릭하면 해당 입력 카드로 스크롤한다
  useEffect(() => {
    if (focusedIndex == null) return;
    const el = cardRefs.current[focusedIndex];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => onFocusHandled?.(), 1600);
    return () => clearTimeout(timer);
  }, [focusedIndex, onFocusHandled]);

  function addWork() {
    if (!canAddWork) return;
    setWorks(prev => [...prev, {
      id: null, title: '', role: '', year: '', company: '', mediaType: '',
      videoUrl: '', stillcuts: [], hidden: false, _isNew: true,
    }]);
  }
  function updateWork(target, field, value) {
    setWorks(prev => prev.map(w => {
      if (w !== target) return w;
      // 레이아웃 전용 필드(hidden)는 서버 저장 대상이 아니므로 _isUpdated를 세우지 않는다
      const touchesServer = field !== 'hidden';
      return { ...w, [field]: value, ...(touchesServer && w.id ? { _isUpdated: true } : {}) };
    }));
  }
  function removeWork(target) {
    if (target.id) onWorkDeleted(target.id);
    setWorks(prev => prev.filter(w => w !== target));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={t('13px', 600, C.sub)}>
          이 페이지 {pageWorks.length}/{WORKS_PER_PAGE} · 전체 {totalWorks}/{MAX_FILMOGRAPHY}
        </span>
        {hiddenCount > 0 && <span style={t('12px', 500, C.g400)}>숨김 {hiddenCount}개</span>}
      </div>

      {pageWorks.map((w, i) => (
        <WorkCard
          key={w.id ?? `p${pageIndex}-${i}`}
          work={w}
          index={pageIndex * WORKS_PER_PAGE + i}
          cardRef={(el) => { cardRefs.current[i] = el; }}
          highlighted={focusedIndex === i}
          onUpdate={(field, value) => updateWork(w, field, value)}
          onRemove={() => removeWork(w)}
        />
      ))}

      <button
        onClick={addWork}
        disabled={!canAddWork}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          height: '46px', borderRadius: '10px', border: `1px solid ${C.g200}`,
          background: C.white, cursor: canAddWork ? 'pointer' : 'default',
          opacity: canAddWork ? 1 : 0.5,
        }}
      >
        <span style={{ fontSize: '16px', color: C.bk }}>+</span>
        <span style={t('14px', 600, C.bk)}>작품 추가</span>
      </button>

      {!canAddWork && (
        <p style={t('12px', 400, C.g400)}>
          {totalWorks >= MAX_FILMOGRAPHY
            ? `작품은 최대 ${MAX_FILMOGRAPHY}개까지 등록할 수 있습니다.`
            : '이 페이지가 가득 찼습니다. 페이지를 추가하면 계속 등록할 수 있습니다.'}
        </p>
      )}

    </div>
  );
}

/* ── 추가 사진 편집 패널 ── */
function PhotosPanel({ photos, slotOffset = 0, setPhotos, selectedSlot, setSelectedSlot, onPhotoMediaRemoved }) {
  const selectedPhoto = selectedSlot == null ? null : normalizePhoto(photos[selectedSlot]);

  function setSlot(idx, value) {
    setPhotos(prev => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }

  /** 슬롯을 비우거나 덮어쓰기 전, 서버에 올라가 있던 사진은 삭제 예약하고 blob은 회수한다 */
  function releaseSlot(idx) {
    const cur = normalizePhoto(photos[idx]);
    if (!cur) return;
    if (cur.mediaId) onPhotoMediaRemoved?.(cur.mediaId);
    if (cur.file && cur.url?.startsWith('blob:')) URL.revokeObjectURL(cur.url);
  }

  function handleFileChange(idx, file) {
    if (!file) return;
    releaseSlot(idx);
    // 사진을 바꾸면 이전 구도는 의미가 없으므로 조정값을 초기화한다
    setSlot(idx, { url: URL.createObjectURL(file), file, mediaId: null, x: 0, y: 0, scale: 1 });
    setSelectedSlot(idx);
  }

  function removePhoto(idx) {
    releaseSlot(idx);
    setSlot(idx, null);
    if (selectedSlot === idx) setSelectedSlot(null);
  }

  /** PhotoAdjuster가 넘기는 갱신 함수를 현재 슬롯에 적용 */
  function updateSelected(updater) {
    setPhotos(prev => {
      const cur = normalizePhoto(prev[selectedSlot]);
      if (!cur) return prev;
      const next = [...prev];
      next[selectedSlot] = updater(cur);
      return next;
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <span style={t('13px', 600, C.sub)}>구역을 선택하여 사진을 업로드하세요</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
        {PHOTO_SLOTS.flat().map((label, i) => {
          const idx = slotOffset + i;
          const photo = normalizePhoto(photos[idx]);
          return (
            <button
              key={idx}
              onClick={() => setSelectedSlot(idx)}
              style={{
                aspectRatio: '1 / 1',
                borderRadius: '10px',
                border: `1px solid ${selectedSlot === idx ? C.bk : C.g200}`,
                background: selectedSlot === idx ? C.bg : C.white,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', padding: 0,
              }}
            >
              {photo
                ? <PhotoFrame photo={photo} alt={label} />
                : <span style={t('11px', 400, C.g300)}>{label}</span>}
            </button>
          );
        })}
      </div>

      {selectedSlot == null ? (
        <p style={t('12px', 400, C.g300)}>위에서 칸을 선택하면 사진을 넣고 위치를 맞출 수 있습니다.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderTop: `1px solid ${C.g200}`, paddingTop: '16px' }}>
          <span style={t('13px', 700, C.bk)}>{PHOTO_SLOTS.flat()[selectedSlot - slotOffset]} 사진</span>

          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            height: '46px', border: `1px solid ${C.g200}`, borderRadius: '10px', cursor: 'pointer',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5-5 5 5M12 5v12" stroke={C.bk} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span style={t('13px', 600, C.bk)}>{selectedPhoto ? '사진 교체' : '사진 업로드'}</span>
            <input
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { handleFileChange(selectedSlot, e.target.files[0]); e.target.value = ''; }}
            />
          </label>

          {selectedPhoto && (
            <>
              <button
                onClick={() => removePhoto(selectedSlot)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  height: '40px', border: 'none', background: 'none', cursor: 'pointer',
                  ...t('13px', 600, '#EF4444'),
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/></svg>
                사진 제거
              </button>

              <PhotoAdjuster photo={selectedPhoto} onChange={updateSelected} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function PortfolioPdfEditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const talentId = searchParams.get('talentId');
  const [activeStep, setActiveStep] = useState('basic');
  const [filmoPages, setFilmoPages] = useState(1);
  const [photoPages, setPhotoPages] = useState(1);
  const [hoveredPageId, setHoveredPageId] = useState(null);
  // 좌측 문서 스크롤 → 현재 지면 판정
  const scrollRef = useRef(null);
  const pageRefs = useRef({});
  const scrollRafRef = useRef(null);
  const scrollLockRef = useRef(false);
  const scrollLockTimerRef = useRef(null);
  // 미리보기에서 클릭한 작품 → 편집 패널에서 해당 카드로 스크롤
  const [focusedWorkIdx, setFocusedWorkIdx] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPhotoSlot, setSelectedPhotoSlot] = useState(null);

  const [originalProfile, setOriginalProfile] = useState(null);
  const [mainPhotoFile, setMainPhotoFile] = useState(null);
  const [existingMainPhotoMediaId, setExistingMainPhotoMediaId] = useState(null);
  const [deletedWorkIds, setDeletedWorkIds] = useState([]);
  const [deletedPhotoMediaIds, setDeletedPhotoMediaIds] = useState([]);

  const [form, setForm] = useState({
    mainPhoto: null, name: '', englishName: '', heightCm: '', weightKg: '',
    birthDate: '', email: '', phone: '', instagram: '', bio: '',
    skills: [], skillDraft: '',
  });
  const [works, setWorks] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    const loadProfile = talentId ? getTalentDetail(talentId) : getMyTalentProfile();
    loadProfile
      .then(async (data) => {
        const medias = data.medias || [];
        const closeUp = medias.find(m => m.photoType === 'PROFILE_CLOSE_UP' && m.url);

        // FILMOGRAPHY 미디어를 workId로 그룹핑 — 프로필 편집 화면과 동일한 규칙
        const stillcutsByWorkId = {};
        medias
          .filter(m => m.photoType === 'FILMOGRAPHY' && m.workId && m.url)
          .forEach(m => {
            if (!stillcutsByWorkId[m.workId]) stillcutsByWorkId[m.workId] = [];
            stillcutsByWorkId[m.workId].push({ mediaId: m.id, preview: m.url });
          });

        // 연도 내림차순 정렬 후 전체 필드 + 스틸컷 매핑
        const profileWorks = [...(data.works || [])]
          .sort((a, b) => Number(b.year || b.releaseYear || 0) - Number(a.year || a.releaseYear || 0))
          .slice(0, MAX_FILMOGRAPHY)
          .map(w => {
            const grouped = stillcutsByWorkId[w.id];
            return {
              id: w.id ?? null,
              title: w.title || '',
              role: w.role || w.roleName || '',
              year: String(w.year || w.releaseYear || ''),
              company: w.productionCompany || '',
              mediaType: w.category || '',
              videoUrl: w.videoUrl || '',
              // 응답 배열 순서는 보장되지 않으므로 mediaId 오름차순 = 먼저 등록한 순으로 정렬한 뒤
              // 맨 앞 1장만 대표로 쓴다. 나머지(프로필 편집 화면에서 올린 것)는 서버에 그대로 둔다.
              stillcuts: (grouped
                ?? (w.stillcutUrl ? [{ mediaId: w.stillcutMediaId ?? null, preview: w.stillcutUrl }] : [])
              )
                .slice()
                .sort((s1, s2) => (s1.mediaId ?? 0) - (s2.mediaId ?? 0))
                .slice(0, MAX_STILLCUTS),
              hidden: false,
            };
          });

        let restoredPhotos = null;
        let restoredWorks = null;
        let restoredFilmoPages = null;
        let restoredPhotoPages = null;
        if (!talentId) {
          try {
            const layout = await getTalentPdfLayout();
            if (layout?.layoutJson) {
              const parsed = JSON.parse(layout.layoutJson);
              // 과거 저장분은 URL 문자열 배열 — 조정값 포함 객체로 승격한다
              if (Array.isArray(parsed.photoSlots)) restoredPhotos = parsed.photoSlots.map(normalizePhoto);
              // workOrder는 작품 ID 배열 — 인덱스는 연도 정렬/작품 증감에 따라 의미가 바뀌어 쓸 수 없다
              if (Array.isArray(parsed.workOrder) && parsed.workOrder.length > 0) {
                const byId = new Map(profileWorks.filter(w => w.id != null).map(w => [w.id, w]));
                restoredWorks = parsed.workOrder.map(id => byId.get(id)).filter(Boolean);
                // 레이아웃 저장 이후 추가된 작품은 뒤에 이어붙여 유실을 막는다
                profileWorks.forEach(w => {
                  if (!restoredWorks.includes(w)) restoredWorks.push(w);
                });
              }
              if (Number.isFinite(parsed.photoPages)) {
                restoredPhotoPages = Math.min(Math.max(0, parsed.photoPages), MAX_PHOTO_PAGES);
              }
              if (Number.isFinite(parsed.filmoPages)) {
                restoredFilmoPages = Math.min(Math.max(1, parsed.filmoPages), MAX_FILMO_PAGES);
              }
              if (Array.isArray(parsed.hiddenWorkIds)) {
                const hiddenSet = new Set(parsed.hiddenWorkIds);
                (restoredWorks ?? profileWorks).forEach(w => {
                  if (w.id != null && hiddenSet.has(w.id)) w.hidden = true;
                });
              }
            }
          } catch {}
        }

        setOriginalProfile(data);
        setExistingMainPhotoMediaId(closeUp?.id ?? null);
        setForm(prev => ({
          ...prev,
          mainPhoto: closeUp?.url || null,
          name: data.name || '',
          englishName: data.englishName || '',
          heightCm: data.heightCm ? String(data.heightCm) : '',
          weightKg: data.weightKg ? String(data.weightKg) : '',
          birthDate: data.birthDate || '',
          email: data.contactEmail || '',
          phone: data.phone || '',
          instagram: data.instagramUrl || '',
          bio: data.bio || '',
          skills: (data.skills || []).map(s => s.name || s.skillName || s).filter(Boolean),
        }));
        const finalWorks = restoredWorks ?? profileWorks;
        setWorks(finalWorks);
        // 저장된 페이지 수가 없으면 작품 수에서 역산 — 8개를 넘으면 자동으로 여러 장이 된다
        setFilmoPages(
          restoredFilmoPages
          ?? Math.min(Math.max(1, Math.ceil(finalWorks.length / WORKS_PER_PAGE)), MAX_FILMO_PAGES)
        );
        if (restoredPhotos) setPhotos(restoredPhotos);
        // 저장된 값이 없으면 슬롯 개수에서 역산 (구버전은 6칸 한 장)
        setPhotoPages(
          restoredPhotoPages
          ?? Math.min(Math.max(1, Math.ceil((restoredPhotos?.length ?? 0) / PHOTOS_PER_PAGE)), MAX_PHOTO_PAGES)
        );
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [talentId]);

  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  const handlePhotoMediaRemoved = useCallback((id) => {
    setDeletedPhotoMediaIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  function handleWorkDeleted(id) {
    setDeletedWorkIds(prev => [...prev, id]);
  }

  async function handleSave() {
    if (isSaving) return;
    // 제작사 미리보기(talentId)는 남의 프로필이라 저장 대상이 없다 —
    // 여기서 저장하면 보는 사람 본인의 레이아웃을 덮어쓴다
    if (talentId) return;
    // 제목이 없는 작품은 서버에 저장되지 않는다. 다른 내용을 입력해 둔 카드라면
    // 조용히 사라지므로 저장 전에 확인을 받는다.
    const droppable = works.filter(w =>
      !w.title && (w.year || w.role || w.company || w.mediaType || w.videoUrl || (w.stillcuts || []).length)
    );
    if (droppable.length > 0) {
      const ok = window.confirm(
        `작품 제목이 비어 있는 항목 ${droppable.length}개는 저장되지 않고 사라집니다.\n계속할까요?`
      );
      if (!ok) return;
    }

    setIsSaving(true);
    try {
      let finalWorks = works;
      let finalPhotos = photos;

      if (originalProfile) {
        // 1. 기본 정보 프로필 동기화
        await updateTalentProfile({
          id: originalProfile.id,
          name: form.name,
          englishName: form.englishName || undefined,
          stageName: originalProfile.stageName,
          gender: originalProfile.gender,
          birthDate: form.birthDate || originalProfile.birthDate,
          // Number('') === 0 이라 `||` 폴백을 쓰면 값을 비울 수 없다
          heightCm: form.heightCm === '' ? undefined : Number(form.heightCm),
          weightKg: form.weightKg === '' ? undefined : Number(form.weightKg),
          bio: form.bio,
          phone: form.phone || undefined,
          residence: originalProfile.residence,
          contactEmail: form.email || undefined,
          visibility: originalProfile.visibility,
          instagramUrl: form.instagram || undefined,
          youtubeUrl: originalProfile.youtubeUrl,
          extraLink: originalProfile.extraLink,
          agency: originalProfile.agency,
        });

        // 2. 대표 이미지 — 교체 / 삭제
        if (mainPhotoFile) {
          const { fileEntityId } = await uploadFile(mainPhotoFile, 'IMAGE');
          if (existingMainPhotoMediaId) {
            await deleteTalentMedia(existingMainPhotoMediaId);
          }
          const newMedia = await addTalentMedia({
            talentProfileId: originalProfile.id,
            fileEntityId,
            mediaType: 'IMAGE',
            photoType: 'PROFILE_CLOSE_UP',
          });
          setExistingMainPhotoMediaId(newMedia?.id ?? null);
          setMainPhotoFile(null);
        } else if (!form.mainPhoto && existingMainPhotoMediaId) {
          // 새 파일 없이 비워둔 경우 = 삭제. 이 분기가 없으면 화면에서만 지워지고
          // 서버 미디어가 남아 다시 열었을 때 사진이 되살아난다.
          await deleteTalentMedia(existingMainPhotoMediaId);
          setExistingMainPhotoMediaId(null);
        }

        // 3. 삭제된 필모그래피 처리
        for (const id of deletedWorkIds) {
          await deleteTalentWork(id);
        }
        setDeletedWorkIds([]);

        // 4. 필모그래피 추가/수정 + 스틸컷 동기화
        // sortOrder는 화면에 보이는 순서(연도 내림차순)를 따라야 한다.
        // 편집기 내부 배열 순서를 그대로 쓰면 프로필 편집 화면의 작품 순서가 엉뚱하게 뒤바뀐다.
        const sortIndexOf = new Map(orderedWorks(works).map((w, i) => [w, i]));
        const updatedWorks = [...works];
        for (let i = 0; i < updatedWorks.length; i++) {
          const w = updatedWorks[i];
          if (!w.title) continue;

          const payload = {
            title: w.title,
            role: w.role || undefined,
            year: w.year ? Number(w.year) : undefined,
            productionCompany: w.company || undefined,
            category: w.mediaType || undefined,
            videoUrl: w.videoUrl || undefined,
            sortOrder: sortIndexOf.get(w) ?? i,
          };

          let workId = w.id;
          if (workId) {
            await updateTalentWork(workId, payload);
          } else {
            const created = await addTalentWork({ talentProfileId: originalProfile.id, ...payload });
            workId = created?.id ?? null;
          }

          // 삭제 대기 중이던 기존 스틸컷 제거
          for (const mediaId of w._deletedStillcutMediaIds || []) {
            await deleteTalentMedia(mediaId).catch(() => {});
          }

          // 새로 첨부한 스틸컷 업로드 (실패해도 전체 저장은 계속)
          const savedStillcuts = [];
          for (const shot of w.stillcuts || []) {
            if (!shot.file) {
              savedStillcuts.push(shot);
              continue;
            }
            if (!workId) continue;
            try {
              const { fileEntityId, url } = await uploadFile(shot.file, 'IMAGE');
              const media = await addTalentMedia({
                talentProfileId: originalProfile.id,
                fileEntityId,
                mediaType: 'IMAGE',
                photoType: 'FILMOGRAPHY',
                workId,
              });
              savedStillcuts.push({ mediaId: media?.id ?? null, preview: url });
            } catch {
              savedStillcuts.push(shot);
            }
          }

          updatedWorks[i] = {
            ...w, id: workId, stillcuts: savedStillcuts,
            _isNew: false, _isUpdated: false, _deletedStillcutMediaIds: [],
          };
        }
        setWorks(updatedWorks);
        finalWorks = updatedWorks;

        // 5. 추가 사진 — 삭제 예약분 정리 후 신규 업로드
        for (const mediaId of deletedPhotoMediaIds) {
          await deleteTalentMedia(mediaId).catch(() => {});
        }
        setDeletedPhotoMediaIds([]);

        const uploadedPhotos = [...photos];
        for (let i = 0; i < uploadedPhotos.length; i++) {
          const slot = normalizePhoto(uploadedPhotos[i]);
          if (!slot?.file) continue;
          try {
            const { fileEntityId, url } = await uploadFile(slot.file, 'IMAGE');
            const media = await addTalentMedia({
              talentProfileId: originalProfile.id,
              fileEntityId,
              mediaType: 'IMAGE',
              photoType: 'PDF_EXTRA',
            });
            // 구도(x·y·scale)는 유지하고 주소만 영구 URL로 교체
            uploadedPhotos[i] = {
              ...slot, url, mediaId: media?.id ?? null, file: null,
            };
            if (slot.url?.startsWith('blob:')) URL.revokeObjectURL(slot.url);
          } catch {
            // 개별 사진 실패는 전체 저장을 막지 않는다 (blob 상태로 남음)
          }
        }
        setPhotos(uploadedPhotos);
        finalPhotos = uploadedPhotos;
      }

      // 6. PDF 레이아웃 저장 — 반드시 필모그래피 동기화 뒤에.
      // 신규 작품은 여기서야 서버 ID를 갖게 되고, 그 전에 저장하면 숨김 상태가 유실된다.
      await saveTalentPdfLayout(JSON.stringify({
        photoSlots: finalPhotos.map(serializePhoto),
        filmoPages,
        photoPages,
        workOrder: finalWorks.map(w => w.id).filter(id => id != null),
        hiddenWorkIds: finalWorks.filter(w => w.hidden && w.id != null).map(w => w.id),
      }));
    } catch {
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false);
    }
  }

  const ordered = orderedWorks(works);
  const worksOfPage = (i) => ordered.slice(i * WORKS_PER_PAGE, (i + 1) * WORKS_PER_PAGE);
  const photosOfPage = (i) => photos.slice(i * PHOTOS_PER_PAGE, (i + 1) * PHOTOS_PER_PAGE);

  const pages = buildPages(filmoPages, photoPages);
  const activePage = pages.find(pg => pg.id === activeStep) ?? pages[0];

  // 다운로드에는 내용이 있는 지면만 담는다 (빈 지면·지워진 지면은 제외)
  const printablePages = pages.filter(pg => {
    if (pg.kind === 'filmography') return worksOfPage(pg.index).some(isPrintableWork);
    if (pg.kind === 'photos') return photosOfPage(pg.index).some(sl => normalizePhoto(sl));
    return true;
  });

  // 추가 버튼은 같은 종류의 마지막 지면 바로 아래에 붙는다.
  // 해당 종류가 아직 없으면 그 자리가 시작될 위치(직전 지면) 뒤에 둔다.
  const lastFilmoId = filmoPages > 0 ? `filmography-${filmoPages - 1}` : 'basic';
  const lastPhotoId =
    photoPages > 0 ? `photos-${photoPages - 1}` : lastFilmoId;

  // 지면을 지워도 남은 자리에 내용이 전부 들어가야 삭제를 허용한다.
  // (필모 페이지는 정렬된 목록을 8개씩 자른 것이라 지우면 뒤 작품이 앞으로 흘러온다)
  const canRemovePage = (pg) => {
    if (pg.kind === 'basic') return false;
    if (pg.kind === 'filmography') return works.length <= (filmoPages - 1) * WORKS_PER_PAGE;
    return true; // 사진 페이지는 내용이 있으면 확인 후 삭제
  };

  const handleFocusHandled = useCallback(() => setFocusedWorkIdx(null), []);

  /** 지정한 지면으로 부드럽게 이동.
   *  이동하는 동안 스크롤 감지를 잠그지 않으면 지나치는 지면마다 우측 패널이 바뀌어 깜빡인다. */
  function scrollToPage(id) {
    const el = pageRefs.current[id];
    if (!el) return;
    scrollLockRef.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.clearTimeout(scrollLockTimerRef.current);
    scrollLockTimerRef.current = window.setTimeout(() => { scrollLockRef.current = false; }, 700);
  }

  function goToPage(id) {
    setFocusedWorkIdx(null);
    setActiveStep(id);
    scrollToPage(id);
  }

  function handleAddFilmoPage() {
    if (filmoPages >= MAX_FILMO_PAGES) return;
    const next = filmoPages + 1;
    setFilmoPages(next);
    setActiveStep(`filmography-${next - 1}`);
    // 새 지면이 그려진 뒤에 스크롤해야 위치를 잡는다
    requestAnimationFrame(() => scrollToPage(`filmography-${next - 1}`));
  }

  function handleAddPhotoPage() {
    if (photoPages >= MAX_PHOTO_PAGES) return;
    const next = photoPages + 1;
    setPhotoPages(next);
    setPhotos(prev => {
      const filled = [...prev];
      filled.length = next * PHOTOS_PER_PAGE;
      return Array.from(filled, v => v ?? null);
    });
    setActiveStep(`photos-${next - 1}`);
    requestAnimationFrame(() => scrollToPage(`photos-${next - 1}`));
  }

  function handleRemovePage(pg) {
    if (!canRemovePage(pg)) return;

    if (pg.kind === 'filmography') {
      const next = filmoPages - 1;
      setFilmoPages(next);
      setFocusedWorkIdx(null);
      if (activeStep === pg.id) setActiveStep(next > 0 ? `filmography-${Math.min(pg.index, next - 1)}` : 'basic');
      return;
    }

    // 사진 페이지 — 해당 6칸을 잘라내고 뒤 페이지를 앞으로 당긴다
    const slots = photosOfPage(pg.index).map(normalizePhoto).filter(Boolean);
    if (slots.length > 0) {
      const ok = window.confirm(`이 지면의 사진 ${slots.length}장이 함께 삭제됩니다. 계속할까요?`);
      if (!ok) return;
    }
    slots.forEach(sl => {
      if (sl.mediaId) handlePhotoMediaRemoved(sl.mediaId);
      if (sl.file && sl.url?.startsWith('blob:')) URL.revokeObjectURL(sl.url);
    });
    const from = pg.index * PHOTOS_PER_PAGE;
    setPhotos(prev => [...prev.slice(0, from), ...prev.slice(from + PHOTOS_PER_PAGE)]);
    setSelectedPhotoSlot(null);
    const next = photoPages - 1;
    setPhotoPages(next);
    if (activeStep === pg.id) {
      setActiveStep(next > 0 ? `photos-${Math.min(pg.index, next - 1)}` : (filmoPages > 0 ? `filmography-${filmoPages - 1}` : 'basic'));
    }
  }

  /** 스크롤 위치로 현재 보고 있는 지면을 판정 — 우측 편집 패널이 따라간다 */
  function handlePreviewScroll() {
    const container = scrollRef.current;
    if (!container || scrollLockRef.current) return;
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const mid = container.scrollTop + container.clientHeight * 0.35;
      let bestId = null;
      let bestDist = Infinity;
      for (const pg of pages) {
        const el = pageRefs.current[pg.id];
        if (!el) continue;
        const dist = Math.abs(el.offsetTop - mid);
        if (dist < bestDist) { bestDist = dist; bestId = pg.id; }
      }
      if (bestId && bestId !== activeStep) {
        setFocusedWorkIdx(null);
        setActiveStep(bestId);
      }
    });
  }

  function handlePdfDownload() {
    // @page는 클래스로 한정할 수 없어 인쇄하는 동안만 주입한다 (다른 페이지 인쇄에 영향 방지)
    const pageStyle = document.createElement('style');
    pageStyle.media = 'print';
    pageStyle.textContent = '@page { size: A4 landscape; margin: 0; }';
    document.head.appendChild(pageStyle);
    document.body.classList.add('pdf-printing');

    const cleanup = () => {
      document.body.classList.remove('pdf-printing');
      pageStyle.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);

    window.print();
    // afterprint를 지원하지 않는 브라우저 대비
    setTimeout(cleanup, 1000);
  }

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className={`pdf-print-host ${styles.page}`} style={{ background: C.bg }}>
      {/* 헤더 */}
      <div className={styles.header} style={{ background: C.white, borderBottom: `1px solid ${C.g200}` }}>
        <div className={styles.headerLeft}>
          <button
            onClick={() => navigate('/talent/profile')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M5 12l7 7M5 12l7-7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span style={t('14px', 400, C.sub)}>닫기</span>
          </button>
          <span style={t('18px', 700, C.bk)}>포트폴리오 PDF 편집</span>
        </div>
        <TopStepper steps={pages} activeStep={activePage.id} onStepClick={goToPage} />
      </div>

      {/* 본문 */}
      <div className={styles.body}>
        {/* 좌측 — 지면을 세로로 이어 붙인 하나의 문서. 실제 PDF처럼 스크롤한다. */}
        <div className={styles.previewPane}>
          <div
            className={styles.previewScroll}
            ref={scrollRef}
            onScroll={handlePreviewScroll}
          >
            {pages.map((pg, i) => (
              <Fragment key={pg.id}>
                <div
                  ref={(el) => { pageRefs.current[pg.id] = el; }}
                  className={styles.previewBox}
                  onMouseEnter={() => setHoveredPageId(pg.id)}
                  onMouseLeave={() => setHoveredPageId(null)}
                  style={{
                    background: C.white,
                    border: `1px solid ${activePage.id === pg.id ? C.bk : C.g200}`,
                  }}
                >
                  {/* 첫 지면(기본 정보)을 제외하고 호버 시 삭제 */}
                  {i > 0 && canRemovePage(pg) && hoveredPageId === pg.id && (
                    <button
                      onClick={() => handleRemovePage(pg)}
                      title="이 지면 삭제"
                      style={{
                        position: 'absolute', top: '10px', right: '10px', zIndex: 2,
                        width: 26, height: 26, borderRadius: '50%',
                        background: C.bk, border: `2px solid ${C.white}`,
                        cursor: 'pointer', padding: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    </button>
                  )}

                  {pg.kind === 'basic' && <BasicInfoPreview form={form} works={works} />}
                  {pg.kind === 'filmography' && (
                    <FilmographyPreview
                      pageWorks={worksOfPage(pg.index)}
                      pageLabel={pg.label}
                      pageNumber={i + 1}
                      onSelectWork={activePage.id === pg.id ? setFocusedWorkIdx : undefined}
                    />
                  )}
                  {pg.kind === 'photos' && (
                    <PhotosPreview
                      photos={photos}
                      slotOffset={pg.index * PHOTOS_PER_PAGE}
                      pageNumber={i + 1}
                      selectedSlot={selectedPhotoSlot}
                      onSlotClick={setSelectedPhotoSlot}
                    />
                  )}
                </div>

                {pg.id === lastFilmoId && (
                  <AddPageButton
                    label="필모그래피 페이지 추가"
                    onClick={handleAddFilmoPage}
                    disabled={filmoPages >= MAX_FILMO_PAGES}
                  />
                )}
                {pg.id === lastPhotoId && (
                  <AddPageButton
                    label="추가 사진 페이지 추가"
                    onClick={handleAddPhotoPage}
                    disabled={photoPages >= MAX_PHOTO_PAGES}
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>

        {/* 편집 패널 — 스크롤로 보고 있는 지면을 따라간다 */}
        <div className={styles.editPane}>
          <div>
            <span style={t('12px', 500, C.sub)}>편집 중인 페이지</span>
            <h2 style={{ ...t('22px', 700, C.bk), margin: '4px 0 0' }}>{activePage.label}</h2>
          </div>

          <div className={styles.editPaneScroll} style={{ borderTop: `1px solid ${C.g200}` }}>
            {activePage.kind === 'basic' && (
              <BasicInfoPanel
                form={form}
                updateField={updateField}
                onPhotoChange={(file) => setMainPhotoFile(file)}
              />
            )}
            {activePage.kind === 'filmography' && (
              <FilmographyPanel
                pageWorks={worksOfPage(activePage.index)}
                pageIndex={activePage.index}
                totalWorks={works.length}
                setWorks={setWorks}
                onWorkDeleted={handleWorkDeleted}
                canAddWork={
                  works.length < MAX_FILMOGRAPHY
                  && worksOfPage(activePage.index).length < WORKS_PER_PAGE
                }
                focusedIndex={focusedWorkIdx}
                onFocusHandled={handleFocusHandled}
              />
            )}
            {activePage.kind === 'photos' && (
              <PhotosPanel
                photos={photos}
                slotOffset={activePage.index * PHOTOS_PER_PAGE}
                setPhotos={setPhotos}
                selectedSlot={selectedPhotoSlot}
                setSelectedSlot={setSelectedPhotoSlot}
                onPhotoMediaRemoved={handlePhotoMediaRemoved}
              />
            )}
          </div>
        </div>
      </div>

      {/* 하단 액션바 */}
      <div className={styles.footer} style={{ background: C.white, borderTop: `1px solid ${C.g200}` }}>
        <button
          className={styles.footerCancelBtn}
          onClick={() => navigate('/talent/profile')}
          style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'none', cursor: 'pointer', ...t('14px', 500, C.sub) }}
        >
          취소
        </button>
        {/* 제작사 미리보기에서는 저장 대상이 없어 버튼을 노출하지 않는다 */}
        {!talentId && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{ padding: '12px 24px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: isSaving ? 'default' : 'pointer', opacity: isSaving ? 0.6 : 1, ...t('14px', 600, C.bk) }}
          >
            {isSaving ? '저장 중...' : '저장'}
          </button>
        )}
        <button
          onClick={handlePdfDownload}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '10px', border: 'none', background: C.bk, cursor: 'pointer', ...t('14px', 600, C.white) }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke={C.white} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          PDF 다운로드
        </button>
      </div>

      {/* 인쇄 전용 — 내용이 있는 지면만, 그 안에서 번호를 다시 매겨 출력한다.
          (빈 지면을 건너뛰면서 화면 인덱스를 쓰면 PDF 쪽번호가 1,3처럼 튄다) */}
      <div className={`pdf-print-root ${styles.printRoot}`} aria-hidden="true">
        {printablePages.map((pg, i) => (
          <div className={styles.printPage} key={`print-${pg.id}`}>
            <div className={styles.printScale}>
              {pg.kind === 'basic' && <BasicInfoPreview form={form} works={works} />}
              {pg.kind === 'filmography' && (
                <FilmographyPreview
                  pageWorks={worksOfPage(pg.index)}
                  pageLabel={pg.label}
                  pageNumber={i + 1}
                />
              )}
              {pg.kind === 'photos' && (
                <PhotosPreview
                  photos={photos}
                  slotOffset={pg.index * PHOTOS_PER_PAGE}
                  pageNumber={i + 1}
                  selectedSlot={null}
                  onSlotClick={() => {}}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
